import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Waves, Lock, Spinner, CheckCircle, WarningCircle, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';
import { Logo, LogoText } from '../components/Logo';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Invalid reset link. Please request a new one.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: newPassword });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may be expired.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <Logo size="lg" />
            <span className="font-black text-2xl tracking-tight">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </Link>
        </div>

        <div className="border rounded-xl p-8" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
          {!token ? (
            <div className="text-center">
              <WarningCircle weight="fill" className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Invalid Reset Link</h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>This password reset link is invalid or has expired.</p>
              <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="back-to-login">Back to Login</Link>
            </div>
          ) : success ? (
            <div className="text-center">
              <CheckCircle weight="fill" className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Password Reset!</h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Your password has been changed successfully.</p>
              <button onClick={() => navigate('/login')} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="go-to-login">Sign In with New Password</button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }}>
                  <Lock weight="duotone" className="w-7 h-7" style={{ color: 'var(--accent)' }} />
                </div>
                <h2 className="text-xl font-bold">Reset Password</h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Enter your new password below</p>
              </div>
              {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 text-sm rounded-lg mb-4" data-testid="reset-error">{error}</div>}
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>New Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className="input-field pr-10" data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
                      {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Confirm Password</label>
                  <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="input-field" data-testid="confirm-password-input" />
                </div>
                <button type="submit" disabled={loading} className="w-full btn-primary flex items-center justify-center gap-2 py-3" data-testid="reset-submit">
                  {loading ? <Spinner className="w-5 h-5 animate-spin" /> : <><Lock weight="bold" className="w-5 h-5" />Reset Password</>}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-sm mt-6" style={{ color: 'var(--text-muted)' }}>
          Remember your password?{' '}
          <Link to="/login" className="font-medium" style={{ color: 'var(--accent)' }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
